import { ChallengeLocationSummary, CheckIn, CreateLocationRequest, CreatePointRequest, CreatePointsResponse, FilterFunction, FilterName, GeocodeType, GetActivatedChallengeResponse, GetChallengeResponse, GetPointResponse, GetTopicResponse, Image, ImageUpload, LatLng, Location, PointOrderingOption, SearchablePoint, TopLevelMarkerHolder, TopicCategory, UserTag } from '../../types';
import { GoogleTagEvent, HttpError, logWithGoogleTagAndTopicOrganization } from '../api/api';
import { Marker, MarkerClusterer, SuperClusterViewportAlgorithm } from '@twitchaxorg/markerclusterer';
import { addNomination, electionNominationsAreHappening } from '../api/election-api';
import { addPoint, addPoints, deletePoint, getAllPointsPaginated, getPoint, getPoints, updatePoint, updatePoints } from '../api/points-api';
import { addPointShapeToMap, createMarkerHolder, getNumberedPointIconColor, getNumberedPointIconShape, preloadCustomIcons, removePointShapeFromMap } from '../markers';
import { escapeCsvValue, saveAs } from '../files';
import { getMarkerPosition, intoPointRequests, isDefined, updateMarkerVisibility } from '../utils';
import { hideShape, removeShapeFromMap } from '../shapes';

import { MarkerService } from '../api/google-api';
import PriorityQueue from '../priority-queue';
import { ProxiClusterRendererSimple } from '../../cluster-rendering';
import { State } from '../state/state-management/state';
import accountInfo from './account-info';
import { areLocationsSame } from '../filters';
import challengeData from './challenge-data';
import featuresInfo from './features-info';
import { getCheckinKey } from '../api/checkin-api';
import { getMainPage } from '../page-name';
import { getQueryStringParameters } from '../urls';
import { makeColorsIntoNumberSvg } from '../colors';
import { processPointImages } from '../components/point-card';
import { property } from '../state/state-management/decorators/property';
import { shouldRevealSpecialOffer } from '../challenge-special-offers';
import topicData from './topic-data';

export type MarkerCallbackFunction = (holder: TopLevelMarkerHolder, locationBeforeDrag?: Location) => void | Promise<void>;

/**
 * Note: the google.maps.Map construct doesnt have a way for you to see the markers that are attached to it.
 * Deleting markers is extremely annoying - you need to set the value of `map` to `null` on the marker and then delete the values by removing references to them
 * https://developers.google.com/maps/documentation/javascript/examples/marker-remove
 *
 * This is why we need to keep track of markers in the way that we are.
 */
class PointCache extends State {
	@property({ type: Boolean }) isReady: boolean = false;

	@property({ type: String }) topic?: GetTopicResponse;

	@property({ type: Object }) map?: google.maps.Map;

	@property({ type: Object }) userLocation?: LatLng;

	@property({ type:  Object }) checkins: Map<String, CheckIn[]> = new Map<String, CheckIn[]>();

	// be careful using this. It filters as people filter. If you need to refer to ALL points, use allPoints
	@property({value: [], type: Array}) points: GetPointResponse[] = [];

	@property({ type: Boolean }) hasFilterApplied: boolean = false;

	@property({ type: PointOrderingOption }) pointOrdering: PointOrderingOption[] = [PointOrderingOption.Number, PointOrderingOption.Alphabetical];

	@property({ type: Number }) pointsRemaining: number = 0;

	allPoints: GetPointResponse[] = [];

	// these are just a way to keep track of all markers for each point. the map is set to null for all of these markers, and should stay that way
	allMarkers: TopLevelMarkerHolder[] = [];
	private activeMarkers: TopLevelMarkerHolder[] = [];

	private readonly allMarkersByPointMap = new Map<string, TopLevelMarkerHolder>();

	markerClusterer?: MarkerClusterer;

	filterFunctions: FilterFunction[] = [];

	private readonly amplitude_callsite: string = 'Point Cache';

	private lastUpdatedTime?: Date = new Date();

	private topicId: string = '';

	private topicKey: string = '';

	markerClickCallback?: MarkerCallbackFunction;

	private markerDragEndCallback?: MarkerCallbackFunction;

	private useMarkerClusterer: boolean = false;

	private refreshTimer?: number;

	advancedMarkers?: boolean = true;

	markersReady: boolean = false;

	categoryFilterList: string[] = [];

	tagFilterList: string[] = [];

	// Dynamic refresh timer properties
	private currentRefreshInterval: number = 30000; // Start at 30 seconds
	private consecutiveEmptyRefreshes: number = 0;
	private refreshStartTime?: Date;
	private totalRefreshCount: number = 0;
	private readonly MIN_REFRESH_INTERVAL = 30000; // 30 seconds
	private readonly MAX_REFRESH_INTERVAL = 600000; // 10 minutes
	private readonly MAX_REFRESH_DURATION = 7200000; // 2 hours
	private readonly MAX_REFRESH_COUNT = 240; // 2 hours worth at 30 second intervals

	private readonly eventTarget = new EventTarget();

	onPointsFilteredOrFetched(listener: EventListener) {
		this.eventTarget.addEventListener('pointsFilteredOrFetched', listener);
	}

	get hasPoints() {
		return this.points.length > 0;
	}

	get allActivePoints() {
		return this.allPoints.filter(point => point.active);
	}

	get orderedPoints() {
		if (isDefined(this.topic) && isDefined(this.pointOrdering)) {
			return this.orderPoints(this.points, this.topic, this.pointOrdering);
		} else {
			return this.orderPoints(this.points);
		}
	}

	get pointsAwaitingApproval() {
		return this.points.filter(point => !point.active);
	}

	get markersInBounds() {
		return this.activeMarkers.filter(marker => this.isMarkerInBounds(marker));
	}

	getPoint(pointId: string) {
		return this.points.find(point => point._id.$oid === pointId);
	}

	getMarker(pointId: string) {
		return this.allMarkersByPointMap.get(pointId);
	}

	// call this on a page to instantiate cache for all children of the page
	// if `reinstantiation` is true, then we are re-instantiating the cache, and we need to reinstantiate the markers
	// `point_id`/`point_key` are an authorization fallback for unique per-point edit links — see
	// `get_topic` server-side fallback. Used by point-page when topic_key is empty.
	async instantiatePointCache(topic: GetTopicResponse, topic_key:string, reinstantiation: boolean = false, clustering: boolean = true, checkins: Map<String, CheckIn[]> = new Map<String, CheckIn[]>(), point_id?: string, point_key?: string) {
		// clear any existing refresh timer
		if (isDefined(this.refreshTimer)) {
			window.clearInterval(this.refreshTimer);
		}

		try {
			const useCache = !reinstantiation && !topicData.isWriter;

			// If we have a ton of points, then we need to optimize.
			let initialPoints;
			if(this.allPoints.length === 0 && topic.statistics.approximate_point_count > 1000) {
				// Await a minimal call.
				initialPoints = await getPoints(this.amplitude_callsite, topic._id.$oid, topic_key, undefined, useCache, true, undefined, undefined, point_id, point_key);

				// Schedule a full call to fetch all points after a bit of a delay (asynchronously), and just replace the underlying data
				setTimeout(async () => {
					// Get all the point data using pagination to reduce payload size
					this.allPoints = await getAllPointsPaginated(this.amplitude_callsite, topic._id.$oid, topic_key, undefined, useCache, false, undefined, point_id, point_key);

					// Update the marker point pointers.
					this.allPoints.forEach(point => {
						const markerHolder = this.allMarkersByPointMap.get(point._id.$oid);
						if (isDefined(markerHolder)) {
							markerHolder.point = point;
						}
					});

					// Fire the proper events, etc.
					this.updatePoints();
				}, 3000);
			} else {
				// Otherwise, make the "full" call right now using pagination for efficiency
				initialPoints = await getAllPointsPaginated(this.amplitude_callsite, topic._id.$oid, topic_key, undefined, useCache, undefined, undefined, point_id, point_key);
			}

			this.checkins = checkins ?? this.checkins;
			this.allPoints = this.points = initialPoints;
			this.allPoints = initialPoints;
			this.lastUpdatedTime = new Date();
			this.topicId = topic._id.$oid;
			this.topic = topic;
			this.topicKey = topic_key;
			// [ARoney] Turn off advanced markers due to perf implications (tons of extra work when processing them on first render).  Left the old logic here for future.
			this.advancedMarkers = false; // topic.custom_map_style_name !== 'custom';
			this.pointOrdering = topic.point_sort_order ?? [PointOrderingOption.Number, PointOrderingOption.Category, PointOrderingOption.Alphabetical];

			// If distance is the first sort option and we don't have user location yet, try to get it
			if (this.pointOrdering[0] === PointOrderingOption.Distance && !this.userLocation) {
				this.requestUserLocation();
			}

			this.isReady = true;
			this.pointsRemaining = featuresInfo.pointsPerMapEnabled - this.allPoints.length;
			// turn off advanced markers for custom styles because google doesnt support them yet (they require a mapID)

			// no clustering on map page bc thats where we take screenshots
			this.useMarkerClusterer = this.allPoints.length > (this.topic?.marker_cluster_threshold ?? 200) && clustering;

			// Initialize dynamic refresh tracking
			this.currentRefreshInterval = this.MIN_REFRESH_INTERVAL;
			this.consecutiveEmptyRefreshes = 0;
			this.refreshStartTime = new Date();
			this.totalRefreshCount = 0;
			if (isDefined(topicData.mapComponent)) {
				// if there is no map component, then we are likely on a page that doesnt show points (e.g., the topic page) and so we dont need to refresh points because they wont be shown anyway
				this.refreshTimer = window.setInterval(this.lookForNewPoints.bind(this), this.currentRefreshInterval);
			}

			if(reinstantiation && isDefined(this.map) && isDefined(this.markerClickCallback) && isDefined(this.markerDragEndCallback) && this.allPoints.length > 0) {
				// remove all existing markers
				this.removeMarkers(this.allMarkers);
				await this.instantiateMarkers(this.map, this.markerClickCallback, this.markerDragEndCallback);
			}
			if (challengeData.challenge && topicData.challengeHappeningNow) {
				await challengeData.setChallengeLocations();
			}
		} catch(e: any) {
			// eslint-disable-next-line @typescript-eslint/restrict-template-expressions
			console.error(`An error has occurred starting poitn cache.  Context: ${e}`);
		}
	}

	async refreshMarkers() {
		if(this.map && this.markerClickCallback && this.markerDragEndCallback) {
			// remove all existing markers
			this.removeMarkers(this.allMarkers);
			await this.instantiateMarkers(this.map, this.markerClickCallback, this.markerDragEndCallback);
		}
	}

	// call this once per route to initialize the markers
	async instantiateMarkers(map: google.maps.Map, markerClickCallback: MarkerCallbackFunction, markerDragEndCallback: MarkerCallbackFunction) {
		if (this.advancedMarkers === true) {
			await MarkerService.loadMarkerElement();
		}
		this.map = map;
		this.markerClickCallback = markerClickCallback;
		this.markerDragEndCallback = markerDragEndCallback;

		// order points
		this.points = this.orderedPoints;

		// Preload all custom icons in parallel before creating markers
		await preloadCustomIcons(this.points, topicData.categories);

		// Create a global for all markers.
		this.allMarkers = this.activeMarkers = this.points.map(point => {
			return this.createMarkerFromPoint(point);
		});

		const markers = this.activeMarkers.flatMap((marker) => {
				if (isDefined(marker.halo)) {
					return [marker.halo.inner, marker.inner];
				} else {
					return [marker.inner];
				}
			}
		);

		this.markerClusterer = new MarkerClusterer({
			map: this.map,
			markers,
			algorithm: new SuperClusterViewportAlgorithm({
				maxZoom: 22,
				radius: 400,
				minPoints: this.useMarkerClusterer ? this.topic?.marker_cluster_minimum : 1_000_000,
			}),
			renderer: new ProxiClusterRendererSimple(),
		});

		// leaving this here in case we want to add it back in later
		// we used to remove the shapes from the map when clustered or out of view but it really confused users. we will leave them on for now.
		// this.markerClusterer.addListener('markerunclustered', (marker: Marker) => {
		// 	// addPointShapeToMap(marker);
		// });

		this.markerClusterer.addListener('markerclustered', (marker: Marker) => {
			// see note from above
			// removePointShapeFromMap(marker);

			// hide extra divs
			const markerHolder = this.allMarkers.find(m => m.inner === marker);
			if (isDefined(markerHolder)) {
				markerHolder.focusDivs?.aMBackgroundCircle?.style.setProperty('display', 'none');
				markerHolder.focusDivs?.aMTextBelow?.style.setProperty('display', 'none');
				markerHolder.focusDivs?.mBackgroundOverlay?.hide();
				markerHolder.focusDivs?.mTextBelowOverlay?.hide();
			}
		});

		this.markersReady = true;

		// Update the points / markers (good idea here in case a filter is already set).
		this.updatePoints();
	}

	// call this when we've made a change to the topic (e.g., added a category) but not necessarily a point
	async updateTopic(topic: GetTopicResponse, topic_key: string) {
		this.topic = topic;
		this.topicKey = topic_key;

		// Update the points / markers (good idea here in case a filter is already set).
		this.updatePoints();
	}

	// use this when you dont actually have the new point data, just the id
	async fetchAndAdd(pointRequest: CreatePointRequest, imagesToUpload: ImageUpload[] = [], imagesToDelete: Image[] = [], geocode_type?: GeocodeType) {
		if (pointCache.allPoints.length >= featuresInfo.pointsPerMapEnabled && (topicData.isWriter || topicData.isOwner))  {
			logWithGoogleTagAndTopicOrganization(this.amplitude_callsite, GoogleTagEvent.POINT_LIMIT_REACHED_ERROR, {
				point_limit: featuresInfo.pointsPerMapEnabled,
				points_count: pointCache.allPoints.length,
			});
			getMainPage().errorToastElement.showToastAndThrow('You have reached the maximum number of points allowed on this map.  Please upgrade to add more.');
		} else if (pointCache.allPoints.length >= featuresInfo.pointsPerMapEnabled) {
			pointRequest.active = false;
			logWithGoogleTagAndTopicOrganization(this.amplitude_callsite, GoogleTagEvent.POINT_LIMIT_REACHED_ERROR, {
				point_limit: featuresInfo.pointsPerMapEnabled,
				points_count: pointCache.allPoints.length,
			});
		}
		let result;

		if (topicData.isWriter && topicData.hasActiveGate) {
			pointRequest.is_gate_hidden = true;
		}

		// nominations are added a little differently
		if (pointRequest.is_nomination === true && electionNominationsAreHappening(topicData.electionHappeningNow)) {
			result = await addNomination (
				this.amplitude_callsite,
				topicData.electionHappeningNow?._id.$oid ?? '',
				pointRequest,
				this.topicKey,
			);
		} else {
			result = await addPoint(
				this.amplitude_callsite,
				this.topicId,
				pointRequest,
				this.topicKey,
				geocode_type,
			);
		}

		const pointId = result._id.$oid;
		if (imagesToUpload.length > 0 || imagesToDelete.length > 0) {
			await processPointImages(imagesToUpload, imagesToDelete, pointId, this.topicId, result.write_key, this.amplitude_callsite);
		}

		const newPoint = await getPoint(this.amplitude_callsite, pointId, this.topicKey);

		this.addNewPointsToCache([newPoint]);

		const event = topicData.isWriter || topicData.isOwner ? GoogleTagEvent.ADMIN_POINT_ADDED : GoogleTagEvent.POINT_ADDED;
		logWithGoogleTagAndTopicOrganization(this.amplitude_callsite, event);

		if (pointRequest.custom_shape && topicData.mapComponent) {
			// this gets rid of the preview image
			await topicData.mapComponent.loadShapesGeoJson();
		}

	}

	async fetchAndAddMany(pointRequests: CreatePointRequest[], geocode_type?: GeocodeType) : Promise<CreatePointsResponse> {
		if (pointCache.allPoints.length >= featuresInfo.pointsPerMapEnabled && (topicData.isWriter || topicData.isOwner))  {
			logWithGoogleTagAndTopicOrganization(this.amplitude_callsite, GoogleTagEvent.POINT_LIMIT_REACHED_ERROR, {
				point_limit: featuresInfo.pointsPerMapEnabled,
				points_count: pointCache.allPoints.length
			});
			getMainPage().errorToastElement.showToastAndThrow('You have reached the maximum number of points allowed on this map.  Please upgrade to add more.');
		}

		const result = await addPoints(
			this.amplitude_callsite,
			this.topicId,
			pointRequests,
			this.topicKey,
			geocode_type,
		);

		const pointIds = result.updated_point_ids;
		const newPoints: GetPointResponse[] = [];

		for (const pointId of pointIds) {
			const newPoint = await getPoint(this.amplitude_callsite, pointId, this.topicKey);
			newPoints.push(newPoint);
		}

		this.addNewPointsToCache(newPoints);

		logWithGoogleTagAndTopicOrganization(this.amplitude_callsite, GoogleTagEvent.ADMIN_MANY_POINTS_ADDED);

		return result;
	}

	async modifyAndFetch(pointToModifyId: string, pointRequest?: CreatePointRequest, modifyMarker: boolean = false, imagesToUpload: ImageUpload[] = [], imagesToDelete: Image[] = [], geocode_type?: GeocodeType, update_map_points = true) {
		// if we pass in a request, we want to update the point and handle it here. if not, assume point was already updated and just fetch and update cache
		if (isDefined(pointRequest)) {
			const queryString = getQueryStringParameters();
			const modificationKey = queryString.get('point_key') ?? this.topicKey; // this may be undefined, if so, we default to topic key

			await updatePoint(
				this.amplitude_callsite,
				this.topicId,
				pointRequest,
				modificationKey,
				geocode_type,
				{ update: 'Activate Suggested Point' }
			);

			await processPointImages(imagesToUpload, imagesToDelete, pointToModifyId, this.topicId, modificationKey, this.amplitude_callsite);
		}

		// Same fallback as `modificationKey` above: prefer URL's point_key (unique edit link) over topic_key.
		const refetchKey = getQueryStringParameters().get('point_key') ?? this.topicKey;
		const pointToModify = await getPoint(this.amplitude_callsite, pointToModifyId, refetchKey);

		// Update our lists.
		this.allPoints = this.allPoints.map(point => {
			if (point._id.$oid === pointToModify._id.$oid) {
				point = pointToModify;
			}
			return point;
		});

		if (update_map_points) {
			// Update the points / markers on the map.
			this.updatePoints();
		}

		if (modifyMarker && this.map) {
			const currentMarker = this.getMarker(pointToModifyId);
			if (currentMarker) {
				this.removeMarker(currentMarker);
				this.allMarkers = this.allMarkers.filter(m => m.id !== currentMarker.id);
			};
			const newMarker = this.createMarkerFromPoint(pointToModify);
			this.allMarkers.push(newMarker);
			this.showMarker(newMarker);
		}
	}

	// people can delete from the point page, so we need to handle the point_key
	async remove(pointId: string) {
		const queryString = getQueryStringParameters();
    	const deletionKey = queryString.get('point_key') ?? this.topicKey; // this may be undefined, if so, we default to topic key

		await deletePoint(
			this.amplitude_callsite,
			this.topicId,
			pointId,
			deletionKey,
		);

		// Find the point object.
		const pointToRemove = this.allPoints.find(point => point._id.$oid === pointId);

		if (isDefined(pointToRemove) && topicData.mapComponent) {
			const holder = this.getShapeHolderForPoint(pointToRemove);
			hideShape(holder?.inner);

			// delete marker
			const marker = this.getMarker(pointId);
			if (marker) {
				this.deleteMarker(marker);
			}
		}

		// Attempt to remove.
		if (isDefined(pointToRemove)) {
			// Update our lists.
			this.allPoints = this.allPoints.filter(point => point._id.$oid !== pointId);

			// Update the points / markers on the map.
			this.updatePoints();
		}
	}

	// Internal method that is designed to be called when the list of points to reflect has likely
	// been changed by another call (e.g., new points fetched, or points filtered).
	// Generally, this should not be called outside of the cache.
	private updatePoints() {
		// Compute (locally) the points that will be active.
		let points = this.allPoints;
		this.filterFunctions.forEach(f => {
			points = points.filter(f.filterFn);
		});

		// Set the new active points.
		this.points = points;
		this.pointsRemaining = featuresInfo.pointsPerMapEnabled - this.points.length;

		if (!this.map) {
			return;
		}
		this.updateMarkers();
	}

	// Internal method that is designed to be called when the list of markers to reflect has likely
	// been changed by another call (e.g., new points fetched, or points filtered).
	// Generally, this should not be called outside of the cache.
	private updateMarkers() {
		if (!this.map) {
			return;
		}
		// First, make sure that we have marker objects for all new points, even if they are filtered out.

		// Compute the points that are brand new.
		const newPoints = this.allPoints.filter(point => !this.allMarkersByPointMap.has(point._id.$oid));

		// Create the markers for the new points.
		const newMarkers = newPoints.map(point => this.createMarkerFromPoint(point));
		this.allMarkers = [...this.allMarkers, ...newMarkers];

		// Next, compute the deltas.

		// Compute the set of markers that are supposed to be displayed.
		const pointIds = new Set(this.points.map(point => point._id.$oid));
		const displayMarkers = this.allMarkers.filter(marker => pointIds.has(marker.point._id.$oid));

		// Compute the markers that need to be added to the global active markers.
		const activeMarkerIds = new Set(this.activeMarkers.map(marker => marker.id));
		const markersToAdd = displayMarkers.filter(marker => !activeMarkerIds.has(marker.id));

		// Compute the markers that need to be removed from the global active markers.
		const displayMarkerIds = new Set(displayMarkers.map(marker => marker.id));
		const markersToRemove = this.activeMarkers.filter(marker => !displayMarkerIds.has(marker.id));

		// Now that we have computed the markers to add and remove, update the global active markers.
		this.activeMarkers = displayMarkers;
		// Add and remove the markers from the map, and re-render.
		if(markersToAdd.length > 0 || markersToRemove.length > 0) {
			this.showMarkers(markersToAdd, true /* noDraw */);
			this.removeMarkers(markersToRemove, true, /* noDraw */);
			// Only render if there are active markers to prevent Supercluster errors with empty data
			if(this.activeMarkers.length > 0) {
				this.markerClusterer?.render();
			}
		}
	}

	filterMarkers(filterFunction?: FilterFunction) {
		// Update the list of filters.
		if (isDefined(filterFunction)) {
			if (this.filterFunctions.some(f => f.name === filterFunction.name)) {
				this.removeFilter(filterFunction.name, false);
			}
			this.filterFunctions.push(filterFunction);
			this.hasFilterApplied = true;
		}

		// Update the points / markers.
		this.updatePoints();
		this.eventTarget.dispatchEvent(new Event('pointsFilteredOrFetched'));
	}

	// if there is no specific filter function name, we remove all filters
	removeFilter(filterFunctionName?: FilterName, shouldUpdatePoints: boolean = true) {
		// Update the list of filters.
		this.filterFunctions = isDefined(filterFunctionName) ? this.filterFunctions.filter(f => f.name !== filterFunctionName) : [];

		// Update the points / markers.
		if(shouldUpdatePoints) {
			this.updatePoints();
		}

		if (this.filterFunctions.length === 0) {
			this.hasFilterApplied = false;
		}
		this.eventTarget.dispatchEvent(new Event('pointsFilteredOrFetched'));
	}

	doesCategoryHavePoints(category: TopicCategory) {
		return this.allActivePoints.some(point => point.topic_category_id.$oid === category._id?.$oid);
	}

	getSameAndSimilarLocationPoints(selectedPoint: GetPointResponse, location?: LatLng) {
		let selectedPointAndOverlappingPoints: GetPointResponse[] = [];
		let nonOverlappingPoints = [];

		if (isDefined(selectedPoint)) {
			selectedPointAndOverlappingPoints = [...this.points].filter(point => {
				return point.location.latLng.lat === selectedPoint.location.latLng.lat && point.location.latLng.lng === selectedPoint.location.latLng.lng;
			});

			nonOverlappingPoints = [...this.points].filter(point => {
				return point.location.latLng.lat !== selectedPoint.location.latLng.lat && point.location.latLng.lng !== selectedPoint.location.latLng.lng;
			});
		} else {
			nonOverlappingPoints = this.points;
		}

		const sortedNonOverlappingPoints = this.orderPoints(nonOverlappingPoints, undefined, [PointOrderingOption.Distance], location);

		return [...selectedPointAndOverlappingPoints, ...sortedNonOverlappingPoints];
	}

	getPointsWithSameLocation(location: CreateLocationRequest)
	{
		return this.allPoints.filter(point => areLocationsSame(point.location, location));
	}

	getPointsFromLocation(location: Location | undefined): GetPointResponse[] {
		if (!isDefined(location)) {
			return [];
		}
		if (isDefined(location.latLng)) {

			const points = this.allPoints.filter(
					p => areLocationsSame(p.location, location)
				);

			return this.orderPoints(points);
		} else {
			return [];
		}
	}

	private isMarkerInBounds(marker: TopLevelMarkerHolder) {
		const position = getMarkerPosition(marker.inner);
		const mapBounds = this.map?.getBounds();
		return isDefined(position) && isDefined(mapBounds) ? mapBounds.contains(position) : false;
	}

	// whenever this function is called (specifically, whenever `createMarkerHolder` is called), the marker is added to the map. we use markersInBounds to keep track,
	// but adding something to visibile markers without calling `createMarkerHolder` does nothing. the only way to add a marker to the map is to create a new one,
	// setting `map` on an existing markerHolder to null removes it from the map, but setting `map` to be a `google.maps.Map` doesnt do anything
	private createMarkerFromPoint(point: GetPointResponse) {
		if (isDefined(this.map) && isDefined(this.markerClickCallback) && isDefined(this.markerDragEndCallback) && isDefined(this.topic)) {
			const markerHolder = createMarkerHolder(point,
													this.map,
													this.topicKey,
													this.checkins,
													this.markerClickCallback,
													this.markerDragEndCallback,
													this.points.length > 200,
													false
													);

			this.allMarkersByPointMap.set(point._id.$oid, markerHolder);

			return markerHolder;
		} else {
			throw new Error('this.map needs to be defined to create a marker and you need to pass in a markerClickCallback');
		}
	}

	private showMarker(marker: TopLevelMarkerHolder, noDraw: boolean = false) {
		if (isDefined(marker.inner) && isDefined(this.markerClusterer)) {
			this.markerClusterer.addMarker(marker.inner, noDraw);

			if (isDefined(marker.halo)) {
				this.markerClusterer.addMarker(marker.halo.inner, noDraw);
			}

			if (isDefined(marker.point.custom_shape)) {
				addPointShapeToMap(marker.inner);
			}
		}
	}

	private showMarkers(markers: TopLevelMarkerHolder[], noDraw: boolean = false) {
		markers.forEach(marker => this.showMarker(marker, true));

		if(!noDraw && this.activeMarkers.length > 0) {
			this.markerClusterer?.render();
		}
	}

	private removeMarker(marker: TopLevelMarkerHolder, noDraw: boolean = false) {
		if (isDefined(marker.inner) && isDefined(this.markerClusterer)) {
			this.markerClusterer.removeMarker(marker.inner, noDraw);

			if (isDefined(marker.halo)) {
				this.markerClusterer.removeMarker(marker.halo.inner, noDraw);
			}

			if (isDefined(marker.point.custom_shape)) {
				removePointShapeFromMap(marker.inner);
			}
		}
	}

	// this is for removing markers from the map temporarily for filtering
	private removeMarkers(markers: TopLevelMarkerHolder[], noDraw: boolean = false) {
		markers.forEach(marker => {
			this.removeMarker(marker, true);
		});

		if(!noDraw && this.activeMarkers.length > 0) {
			this.markerClusterer?.render();
		}
	}

	// this is only for fully removing markers from the map for good, not hiding them temporarily for filtering
	private deleteMarker(marker: TopLevelMarkerHolder) {
		this.removeMarker(marker, true);

		updateMarkerVisibility(marker.inner, false);
		if (isDefined(marker.halo)) {
			updateMarkerVisibility(marker.halo.inner, false);
		}

		if (isDefined(marker.point.custom_shape)) {
			removeShapeFromMap(marker.customShape?.inner);
		}
	}

	getShapeHolderForPoint(point: GetPointResponse) {
		return this.allMarkers.find(marker => marker.point._id.$oid === point._id.$oid)?.customShape;
	}

	async refreshPoint(pointId: string) {
		const updatedPoint = await getPoint(this.amplitude_callsite, pointId, this.topicKey);

		// Update our list.
		this.allPoints = this.allPoints.map(point => {
			if (point._id.$oid === updatedPoint._id.$oid) {
				point = updatedPoint;
			}
			return point;
		});

		// Update the points / markers on the map.
		this.updatePoints();
	}

	private async lookForNewPoints(useCache?: boolean) {
		try{
			// Check if we should stop refreshing due to timeout
			this.totalRefreshCount++;
			const timeSinceStart = this.refreshStartTime ? new Date().getTime() - this.refreshStartTime.getTime() : 0;

			if (this.totalRefreshCount >= this.MAX_REFRESH_COUNT || timeSinceStart >= this.MAX_REFRESH_DURATION) {
				if (isDefined(this.refreshTimer)) {
					window.clearInterval(this.refreshTimer);
					this.refreshTimer = undefined;
				}
				return;
			}

			// Capture the query time BEFORE making the API call so we don't miss points
			// added during the API call and processing
			const queryStartTime = new Date();

			// Add a 30-second buffer to the created_since time to handle timing precision
			// and clock skew. Duplicates are filtered out later anyway.
			const createdSinceWithBuffer = this.lastUpdatedTime ? new Date(this.lastUpdatedTime.getTime() - 30000) : undefined;

			const newPointsSinceLastUpdate = await getPoints(
				this.amplitude_callsite,
				this.topicId,
				this.topicKey,
				createdSinceWithBuffer,
				useCache,
			);

			// newPointsSinceLastUpdate can contain points this user just added, in which case we dont want to add them to map again
			const newPointsNotOnMap = newPointsSinceLastUpdate.filter(point => !this.allPoints.some(p => p._id.$oid === point._id.$oid));

			// Adjust refresh interval based on whether we found new points
			if (newPointsNotOnMap.length > 0) {
				// Found new points - reset to minimum interval and reset timeout counters
				this.consecutiveEmptyRefreshes = 0;
				this.refreshStartTime = new Date();
				this.totalRefreshCount = 0;
				if (this.currentRefreshInterval !== this.MIN_REFRESH_INTERVAL) {
					this.currentRefreshInterval = this.MIN_REFRESH_INTERVAL;
					this.restartRefreshTimer();
				}
			} else {
				// No new points - increase interval progressively
				this.consecutiveEmptyRefreshes++;

				// After 1 empty refresh, start increasing the interval
				if (this.consecutiveEmptyRefreshes >= 1) {
					const newInterval = Math.min(
						this.currentRefreshInterval * 2, // Increase by 100%
						this.MAX_REFRESH_INTERVAL
					);

					if (newInterval !== this.currentRefreshInterval) {
						this.currentRefreshInterval = newInterval;
						this.restartRefreshTimer();
					}
				}
			}

			this.addNewPointsToCache(newPointsNotOnMap); // what happens when people delete points?
			// Use the query start time (captured before the API call) as the baseline for the next query
			// This ensures we don't miss points added during processing
			this.lastUpdatedTime = queryStartTime;
		} catch (e: any) {
			console.error(`An error occurred while refreshing points`);
			// The map is gone; polling it again every half minute helps nobody.
			if (e instanceof HttpError && (e.status === 404 || e.status === 410) && isDefined(this.refreshTimer)) {
				window.clearInterval(this.refreshTimer);
				this.refreshTimer = undefined;
			}
		}
	}

    private restartRefreshTimer() {
        if (isDefined(this.refreshTimer)) {
            window.clearInterval(this.refreshTimer);
        }
        this.refreshTimer = window.setInterval(this.lookForNewPoints.bind(this), this.currentRefreshInterval);
    }

	// FOR EXTERNAL CALLS TO REFRESH
	async refreshPoints(useCache?: boolean) {
		try {
			const newPointsSinceLastUpdate = await getPoints(
				this.amplitude_callsite,
				this.topicId,
				this.topicKey,
				this.lastUpdatedTime,
				useCache,
			);

			// newPointsSinceLastUpdate can contain points this user just added, in which case we dont want to add them to map again
			const newPointsNotOnMap = newPointsSinceLastUpdate.filter(point => !this.allPoints.some(p => p._id.$oid === point._id.$oid));

			this.addNewPointsToCache(newPointsNotOnMap); // what happens when people delete points?
		} catch(e) {
			throw new Error('An error has occurred.  Context: could not refresh the points from the server.');
		}
	}

	async challengePointRefresh() {
		const allPoints = await getAllPointsPaginated(
			this.amplitude_callsite,
			this.topicId,
			this.topicKey,
			undefined,
			false,
		);
		const newPoints = allPoints.filter(point => !this.allPoints.some(p => p._id.$oid === point._id.$oid));
		this.addNewPointsToCache(newPoints);
	}

	getSortedPoints(location?: LatLng, allPoints: boolean = false): GetPointResponse[] {
		return allPoints ? this.orderPoints(this.allPoints, this.topic, this.pointOrdering, location) : this.orderPoints(this.points, this.topic, this.pointOrdering, location);
	}

	private orderPoints(points: GetPointResponse[], topic?: GetTopicResponse, pointOrdering?: PointOrderingOption[], latLng?: LatLng) {
		const order = pointOrdering ?? this.pointOrdering;

		// Separate featured and non-featured points
		const featuredPoints = points.filter(p => p.featured);
		const nonFeaturedPoints = points.filter(p => !p.featured);

		// Sorting function as before
		const sortFn = (p1: GetPointResponse, p2: GetPointResponse) => {
			const results: number[] = [];
			for (let i = 0; i < order.length; i++) {
				if (order[i] === PointOrderingOption.Category) {
					const usedTopic = topic ?? this.topic ?? topicData.topic;
					if (!isDefined(usedTopic)) {
						results.push(0);
					} else {
						const p1CategoryIndex = usedTopic.categories.findIndex(c => c._id?.$oid === p1.topic_category_id?.$oid);
						const p2CategoryIndex = usedTopic.categories.findIndex(c => c._id?.$oid === p2.topic_category_id?.$oid);

						if (p1CategoryIndex !== -1 && p2CategoryIndex !== -1) {
							results.push(p1CategoryIndex - p2CategoryIndex);
						} else if (p1CategoryIndex !== -1) {
							results.push(-1);
						} else if (p2CategoryIndex !== -1) {
							results.push(1);
						} else {
							results.push(0);
						}
					}
				} else if (order[i] === PointOrderingOption.Alphabetical) {
					results.push(`${p1.name ?? 'zzzzz'}${p1.location.search}`.localeCompare(`${p2.name ?? 'zzzzz'}${p2.location.search}`));
				} else if (order[i] === PointOrderingOption.Distance) {
					if (!isDefined(google?.maps?.geometry?.spherical)) {
						results.push(0);
					} else {
						const location = latLng ?? this.userLocation ?? this.map?.getCenter() ?? topic?.default_map_center.latLng;
						if (!location) {
							results.push(0);
						} else {
							const p1Distance = google.maps.geometry.spherical.computeDistanceBetween(location, p1.location.latLng);
							const p2Distance = google.maps.geometry.spherical.computeDistanceBetween(location, p2.location.latLng);
							results.push(p1Distance - p2Distance);
						}
					}
				} else if (order[i] === PointOrderingOption.Number) {
					results.push(Number(p1.number === undefined ? 1000 : p1.number) - Number(p2.number === undefined ? 1000 : p2.number));
				} else if (order[i] === PointOrderingOption.Start) {
					const defaultDate = new Date('2100-01-01').getTime();
					results.push((new Date(p1.start ?? defaultDate)).getTime() - (new Date(p2.start ?? defaultDate)).getTime());
				} else {
					results.push(0);
				}
			}
			for (let i = 0; i < results.length; i++) {
				if (results[i] !== 0) {
					return results[i];
				}
			}
			return 0;
		};

		// Sort both arrays
		featuredPoints.sort(sortFn);
		nonFeaturedPoints.sort(sortFn);

		// Concatenate featured points first
		return [...featuredPoints, ...nonFeaturedPoints];
	}

	private addNewPointsToCache(newPoints: GetPointResponse[]) {
		if(newPoints.length === 0) {
			return;
		}

		// Update our list.
		this.allPoints = [...this.allPoints, ...newPoints];
		// Update the points / markers on the map.
		this.updatePoints();
	}

	get seearchableTerms() {
		const searchablePoints: SearchablePoint[] = this.points.map(point => {
			const terms = [
				point.name,
				point.location.search,
				...(point.tag_info?.user_tags ?? []).map(t => t.name),
				topicData.getCategory(point.topic_category_id?.$oid)?.name
			].filter(term => term !== undefined) as string[];
			return {
				point,
				terms
			};
		});
		return searchablePoints;
	}

	searchableTextForPoint(point: GetPointResponse) {
		const terms = [
			point.name?.toLowerCase(),
			point.location.search?.toLowerCase(),
			...(point.tag_info?.user_tags ?? []).map(t => t.name.toLowerCase()),
			topicData.getCategory(point.topic_category_id?.$oid)?.name.toLowerCase()
		].filter(term => term !== undefined) as string[];
		return terms.join(' ');
	}

	private requestUserLocation() {
		if (!isDefined(navigator.geolocation)) return;
		navigator.geolocation.getCurrentPosition(
			(position) => {
				this.userLocation = { lat: position.coords.latitude, lng: position.coords.longitude };
			},
			() => { /* silently fall back to map center */ },
			{ enableHighAccuracy: false, timeout: 5000, maximumAge: 60000 }
		);
	}

	getXNearestPoints(latLng: LatLng, k: number): GetPointResponse[] {
		if (!isDefined(google?.maps?.geometry?.spherical)) return [];

		// PriorityQueue will now act like a max-heap by storing negative distances.
		const pq = new PriorityQueue();

		// Iterate through all points (filtered) and maintain only k nearest ones.
		pointCache.points.forEach(point => {
			const distance = google.maps.geometry.spherical.computeDistanceBetween(latLng, point.location.latLng);
			// Enqueue using the negative distance.
			pq.enqueue(point, -distance);
			// If we exceed k points, remove the one with the largest distance (i.e. smallest negative value).
			if (pq.size() > k) {
			pq.dequeue();
			}
		});

		// Extract the k nearest points.
		const nearestPoints = pq.toArray();

		// Optionally, sort them by actual distance (closest first)
		nearestPoints.sort((a, b) => {
			const distanceA = google.maps.geometry.spherical.computeDistanceBetween(latLng, a.location.latLng);
			const distanceB = google.maps.geometry.spherical.computeDistanceBetween(latLng, b.location.latLng);
			return distanceA - distanceB;
		});

		return nearestPoints;
	}

	async syncPointTags(points: GetPointResponse[], updatedTags: UserTag[]) { // this can contain subtags that are on the point
        const allTags = updatedTags.flatMap(tag => [tag, ...(tag.tags ?? [])]);

        // update points with new tag data or delete non-existent tags
        const updatedPoints = points.map((point) => {
            point.tag_info = {
                google_tags: point.tag_info?.google_tags ?? [],
                user_tags: point.tag_info?.user_tags
                    .map((tag) => {
                        const updatedTag = allTags.find(
                            (relevantTag) => relevantTag._id?.$oid === tag._id?.$oid
                        );
                        if (updatedTag) {
                            return updatedTag;
                        } else {
                            return null;
                        }
                    })
                    .filter((tag) => tag !== null) as UserTag[]
            };

            return point;
        });

        await updatePoints(this.amplitude_callsite, topicData.topicId, intoPointRequests(updatedPoints), topicData.topicKey);
		if (topicData.topic) {
			await this.instantiatePointCache(topicData.topic, topicData.topicKey, true);
		}

		if (topicData.mapComponent) {
			await topicData.mapComponent.closeListOfPoints();
		}
  }

	async updateCheckIns(pointToModify: GetPointResponse) {
		await accountInfo.refreshCheckins();
		this.checkins = accountInfo.account_checkins;

		// Fix any markers that need to be updated.
		let newMarker: TopLevelMarkerHolder | undefined;
		this.allMarkers = this.allMarkers.map(marker => {
			// If there is a point to modify and it matches the current marker, or if there is no point to modify
			if (marker.point._id.$oid === pointToModify._id.$oid) {
				this.removeMarker(marker);
				newMarker = this.createMarkerFromPoint(pointToModify ?? marker.point); // create the new one
				// add new marker to all markers
				this.showMarker(newMarker);
				return newMarker;
			}
			return marker;
		});

		// Update the points / markers on the map.
		// this.updatePoints();
	}

	// write a function to look for potential duplicates identify a potential duplicate if it has the same category, lat and long
	// return string array of pointids
	getDuplicates() {
		const duplicates = new Set<string>();
		const points = this.allPoints;

		// Sort the points array based on the key
		points.sort((a, b) => {
			const keyA = `${a.location.latLng.lat}-${a.location.latLng.lng}-${a.topic_category_id?.$oid}`;
			const keyB = `${b.location.latLng.lat}-${b.location.latLng.lng}-${b.topic_category_id?.$oid}`;
			return keyA.localeCompare(keyB);
		});

		// Use two-pointer technique to find duplicates
		let i = 0;
		let j = 1;
		while (j < points.length) {
			const keyI = `${points[i].location.latLng.lat}-${points[i].location.latLng.lng}-${points[i].topic_category_id?.$oid}`;
			const keyJ = `${points[j].location.latLng.lat}-${points[j].location.latLng.lng}-${points[j].topic_category_id?.$oid}`;

			if (keyI === keyJ) {
				duplicates.add(points[j]._id.$oid);
				j++;
			} else {
				i = j;
				j++;
			}
		}

		return Array.from(duplicates);
	}

	pointCheckinStatus(p: GetPointResponse) {
        if (isDefined(p)) {
            const isCheckedin = challengeData.allCheckins.has(getCheckinKey(p.location));
            let showSpecialOffer = true;

            if (topicData.hasChallenge && challengeData.challenge) {
				showSpecialOffer = shouldRevealSpecialOffer({
					challenge: challengeData.challenge,
					isCheckedIn: isCheckedin,
					isGuide: false,
				});
            }
			return {isCheckedin, showSpecialOffer};
        }
    }

	isUserCheckedIn(p: GetPointResponse) {
        return this.checkins.has(getCheckinKey(p.location));
    }

	getChallengeLocations(topic: GetTopicResponse, challenge: GetChallengeResponse, activated_challenge?: GetActivatedChallengeResponse): ChallengeLocationSummary[] {
		const challengeLocations: ChallengeLocationSummary[] = [];
		const challengeLocationsMap = new Map();
		const visitedLocationsMap = new Map();

		challenge?.locations.forEach(location => {
			if (location.is_active && typeof location.location === 'object') {
				const key = `${location.location.latLng.lat}-${location.location.latLng.lng}-${location.location.place_id}`;
				challengeLocationsMap.set(key, location);
			}
		});

		activated_challenge?.checkin_locations?.forEach(location => {
			const key = `${location.latLng.lat}-${location.latLng.lng}-${location.place_id}`;
			visitedLocationsMap.set(key, location);
		});

		const gave_up_ids = activated_challenge?.scavenger_hunt_use?.give_up_location_ids.map(id => id.$oid) ?? [];
		const used_hint_ids = activated_challenge?.scavenger_hunt_use?.hint_location_ids.map(id => id.$oid) ?? [];

		this.allPoints.forEach(point => {
			const key = `${point.location.latLng.lat}-${point.location.latLng.lng}-${point.location.place_id}`;
			if (challengeLocationsMap.has(key)) {
				const icon = point.custom_icon_url ??
					(topic.enable_point_numbering === true && point.number !== undefined ? makeColorsIntoNumberSvg(
                        getNumberedPointIconColor(topicData.topic, point.topic_category_id.$oid),
						point.number,
						getNumberedPointIconShape(topicData.topic, point.topic_category_id.$oid)
					) : topic.categories.find(c => c._id?.$oid === point.topic_category_id?.$oid)?.icon);

				const location: ChallengeLocationSummary = {
					point_info: point,
					challenge_location_info: challengeLocationsMap.get(key),
					checked_in: visitedLocationsMap.has(key),
					icon: icon ?? '',
					geo_location_eligible: undefined,
					distance: undefined,
					used_hint: used_hint_ids.includes(point._id.$oid),
					gave_up: gave_up_ids.includes(point._id.$oid),
					earned_points: 0,
				};

				if (location.checked_in===true && location.gave_up !== true) {
					if (activated_challenge?.counted_checkins && activated_challenge?.counted_checkins.length > 0) {
						const all_valid_checkins = activated_challenge.counted_checkins.filter(checkin => checkin.location_id.$oid === location.challenge_location_info?._id?.$oid);
						const totalPoints = all_valid_checkins.reduce((acc, checkin) => acc + (checkin.point_value ?? 0), 0);
						location.earned_points = totalPoints;
					} else {
						location.earned_points = location.challenge_location_info?.value ?? 0;
					}

				}
				if (location.used_hint === true && location.checked_in === true) {
					location.earned_points = (location.earned_points ?? 0) - (challenge?.scavenger_hunt_settings?.hint_use_deduction ?? 0);
				}

				challengeLocations.push(location);
			}
		});

		return challengeLocations;
	}

	onDownloadCsv(allPoints: boolean = false, includeEditLinks: boolean = false) {
        let content = '';

        const categoryMap = new Map<string, TopicCategory>();
        for (const category of topicData.categories ?? []) {
            if (category._id === undefined) return;
            categoryMap.set(category._id.$oid, category);
        }

        // add heading for custom fields
        const customFields = topicData.topic?.add_point_customization?.custom_fields ?? [];

		content +=
			`${escapeCsvValue('Location')},${escapeCsvValue('Category')},${escapeCsvValue('Tags')}`;

		if ((topicData.topic?.enable_point_numbering) === true) {
			content += `,${escapeCsvValue('Number')}`;
		}

		content += `,${escapeCsvValue('Title')},${escapeCsvValue('Description')}`;

		if ((topicData.topic?.has_dates) === true) {
			content += `,${escapeCsvValue('Start Date')},${escapeCsvValue('End Date')}`;
		}

        // add custom fields to header
        for (const field of customFields) {
            content += `,${escapeCsvValue(field.customizations.header ?? `custom_${field._id?.$oid ?? ''}`)}`;
        }

		content +=
			`,${escapeCsvValue('URL')},${escapeCsvValue('Special Offer')},${escapeCsvValue('Submission Email')},${escapeCsvValue('Created At')},${escapeCsvValue('Updated At')},${escapeCsvValue('Proxi Point Id')},${escapeCsvValue('Category Id')},${escapeCsvValue('Upvotes')},${escapeCsvValue('Is Active')}`;

		if (includeEditLinks) {
			content += `,${escapeCsvValue('Secret Edit Link')}`;
		}

        content += '\n';

        const points: GetPointResponse[] = allPoints ? this.allPoints : this.points;

        for (const point of points) {
            const category = categoryMap.get(point.topic_category_id.$oid);
			const location = point.location.search === 'custom' || point.location.search === 'shape' ? `${point.location.latLng.lat}, ${point.location.latLng.lng}` : point.location.search;

			content += `${escapeCsvValue(location)},${escapeCsvValue(category?.name ?? '')},${escapeCsvValue(point.tag_info?.user_tags.map(t => t.name).join(', '))}`;

			if ((topicData.topic?.enable_point_numbering) === true) {
				content += `,${escapeCsvValue(point.number)}`;
			}

			content += `,${escapeCsvValue(point.name)},${escapeCsvValue(point.description)}`;

			if ((topicData.topic?.has_dates) === true) {
				content += `,${escapeCsvValue(point.start)},${escapeCsvValue(point.end)}`;
			}

            for (const field of customFields) {
                const entries = point.custom_information?.custom_field_entries?.filter((entry) => {
                    return entry.field_id.$oid === field._id?.$oid;
                }).map((entry) => escapeCsvValue(entry.field_value)) ?? [''];
                const entry = entries.join(',');

                content += entry.length <= 1 ? `,${entry}`: `,${escapeCsvValue(entry)}`;
            }

			content += `,${escapeCsvValue(point.url)},${escapeCsvValue(point.special_offer?.description ?? '')},${escapeCsvValue(point.email)},${escapeCsvValue(point.created_at)},${escapeCsvValue(point.updated_at)},${escapeCsvValue(point._id.$oid)},${escapeCsvValue(point.topic_category_id.$oid)},${escapeCsvValue(point.likes?.length.toString())},${escapeCsvValue(point.active.toString())}`;

			if (includeEditLinks) {
				content += `,${escapeCsvValue(point.write_link)}`;
			}

            content += '\n';
        }

        const filename = 'points.csv';

        const blob = new Blob([content], {
            type: 'text/plain;charset=utf-8',
        });

        saveAs(blob, filename);
    }
}



const pointCache = new PointCache();

export default pointCache;

